/**
 * 플레이어 진행 상태(HP/MP/EXP/레벨/메소/인벤토리/AP).
 * 씬과 독립적으로 살아있고, 변경 시 이벤트를 발행한다. UI 는 이벤트만 구독한다.
 */

import Phaser from 'phaser';
import { EXP_TABLE, PLAYER, PROGRESSION, RESPAWN, STATS, type PrimaryStat } from '../data/balance';
import { type EquipSlot } from '../data/equipment';
import { ITEMS, STARTING_INVENTORY, STARTING_MESO, type ItemTab } from '../data/items';
import { DEFAULT_JOB_ID, getJob, isFirstJob, jobOwnsSkill, JOBS } from '../data/jobs';
import { ACHIEVE_ORDER, ACHIEVEMENTS, isAchieveEarned } from '../data/achievements';
import { DEFAULT_TITLE, TITLE_ORDER, TITLES, getTitle, isTitleEarned } from '../data/titles';
import { getShop } from '../data/npcs';
import { getQuest } from '../data/quests';
import { getSkillEntry, resolveSkill } from '../data/skills';
import { STATUSES, STATUS_ORDER, type StatusId } from '../data/status';
import { getWeapon, STARTER_WEAPON_ID } from '../data/weapons';
import { EV, type StatusEffectView } from '../game/events';
import { Inventory } from './Inventory';
import {
  applyScrollToGear,
  gearPrice,
  isGearItem,
  rollGear,
  starterGear,
  sumWorn,
  type GearPiece,
} from './gear';
import { requiredLevel } from './jobAdvance';
import {
  fillQuestText,
  questPhase,
  questProgress,
  type QuestSave,
} from './questLogic';
import { clampAp, createStartingAp, deriveStats, basicAttackForJob, type AbilityPoints, type DerivedStats } from './stats';

export type { AbilityPoints, DerivedStats } from './stats';
export type { GearPiece } from './gear';

export const SAVE_VERSION = 8 as const;

export interface SaveData {
  version: typeof SAVE_VERSION;
  name: string;
  titleId: string;
  unlockedTitles: string[];
  unlockedAchievements: string[];
  maxCombo: number;
  kills: Record<string, number>;
  level: number;
  exp: number;
  hp: number;
  mp: number;
  meso: number;
  inventory: { tab: ItemTab; index: number; itemId: string; count: number; gear?: GearPiece }[];
  mapId: string;
  spawnId: string;
  playtimeMs: number;
  savedAt: number;
  ap: AbilityPoints;
  weaponId: string;
  jobId: string;
  sp: number;
  skillLevels: Record<string, number>;
  quickSlots: string[];
  quests: Record<string, QuestSave>;
  worn: Partial<Record<EquipSlot, GearPiece | null>>;
}

export class PlayerState {
  readonly events = new Phaser.Events.EventEmitter();
  readonly inventory = new Inventory();

  level: number = PLAYER.startLevel;
  exp = 0;
  meso = STARTING_MESO;
  hp: number;
  mp: number;
  playtimeMs = 0;
  mapId = '';
  spawnId = '';
  name = PLAYER.defaultName;
  titleId = DEFAULT_TITLE;
  unlockedTitles: string[] = [DEFAULT_TITLE];
  unlockedAchievements: string[] = [];
  maxCombo = 0;
  kills: Record<string, number> = {};
  ap: AbilityPoints = createStartingAp(DEFAULT_JOB_ID);
  weaponId: string = STARTER_WEAPON_ID;
  jobId: string = DEFAULT_JOB_ID;
  sp = PROGRESSION.startSp;
  skillLevels: Record<string, number> = {};
  quickSlots: string[] = [];
  quests: Record<string, QuestSave> = {};
  worn: Partial<Record<EquipSlot, GearPiece | null>> = {};
  pendingScroll: { tab: ItemTab; index: number } | null = null;

  private regenHpTimer = 0;
  private regenMpTimer = 0;
  private readonly effects = new Map<StatusId, { remainMs: number; tickAcc: number }>();
  private readonly buffs = new Map<
    string,
    { remainMs: number; defenseMul: number; damageMul: number; moveMul: number; icon: string; name: string }
  >();

  constructor() {
    this.applyJobDefaults(DEFAULT_JOB_ID, false);
    const s = this.stats;
    this.hp = s.maxHp;
    this.mp = s.maxMp;
    this.fillStarterItems();
  }

  get stats(): DerivedStats {
    return deriveStats(this.level, this.ap, getWeapon(this.weaponId), this.jobId, sumWorn(this.worn));
  }

  get job() {
    return getJob(this.jobId);
  }

  get title() {
    return getTitle(this.titleId);
  }

  get combatDefense(): number {
    return Math.floor(this.stats.defense * this.buffDefenseMul);
  }

  get buffDamageMul(): number {
    let mul = 1;
    for (const b of this.buffs.values()) mul *= b.damageMul;
    return mul;
  }

  get buffDefenseMul(): number {
    let mul = 1;
    for (const b of this.buffs.values()) mul *= b.defenseMul;
    return mul;
  }

  get weapon() {
    return getWeapon(this.weaponId);
  }

  get expNeeded(): number {
    if (this.level >= STATS.maxLevel) return 0;
    return EXP_TABLE[this.level - 1] ?? 0;
  }

  get alive(): boolean {
    return this.hp > 0;
  }

  get moveSpeedMul(): number {
    let mul = 1;
    for (const id of this.effects.keys()) {
      const move = STATUSES[id].moveMul;
      if (move !== undefined) mul *= move;
    }
    for (const b of this.buffs.values()) mul *= b.moveMul;
    return mul;
  }

  hasStatus(id: StatusId): boolean {
    return this.effects.has(id);
  }

  activeStatuses(): StatusEffectView[] {
    const views: StatusEffectView[] = [];
    for (const id of STATUS_ORDER) {
      const fx = this.effects.get(id);
      if (!fx) continue;
      const def = STATUSES[id];
      views.push({ id, name: def.name, icon: def.icon, remainMs: fx.remainMs });
    }
    for (const [id, fx] of this.buffs) {
      views.push({ id, name: fx.name, icon: fx.icon, remainMs: fx.remainMs });
    }
    return views;
  }

  /** 같은 종류는 지속시간만 갱신한다. 새로 걸리면 true */
  applyStatus(id: StatusId): boolean {
    if (!this.alive) return false;
    const def = STATUSES[id];
    const fresh = !this.effects.has(id);
    this.effects.set(id, { remainMs: def.durationMs, tickAcc: 0 });
    this.emitStatus();
    if (fresh) this.events.emit(EV.toast, { message: def.name, tone: 'warn' });
    return fresh;
  }

  clearStatuses(): void {
    if (this.effects.size === 0) return;
    this.effects.clear();
    this.emitStatus();
  }

  /** 매 프레임 호출. 자연 회복 처리. */
  update(deltaMs: number): void {
    this.playtimeMs += deltaMs;
    if (!this.alive) {
      this.clearStatuses();
      return;
    }

    this.tickStatuses(deltaMs);
    this.tickBuffs(deltaMs);
    if (!this.alive) return;

    this.regenHpTimer += deltaMs;
    if (this.regenHpTimer >= STATS.hpRegen.intervalMs) {
      this.regenHpTimer = 0;
      if (this.hp < this.stats.maxHp) this.heal(STATS.hpRegen.amount, 0, false);
    }

    this.regenMpTimer += deltaMs;
    if (this.regenMpTimer >= STATS.mpRegen.intervalMs) {
      this.regenMpTimer = 0;
      if (this.mp < this.stats.maxMp) this.heal(0, STATS.mpRegen.amount, false);
    }
  }

  damage(amount: number): void {
    if (!this.alive) return;
    this.hp = Math.max(0, this.hp - amount);
    this.emitHp();
    if (this.hp <= 0) this.events.emit(EV.playerDied, {});
  }

  heal(hp = 0, mp = 0, notify = true): void {
    const s = this.stats;
    if (hp > 0) {
      this.hp = Math.min(s.maxHp, this.hp + hp);
      this.emitHp();
    }
    if (mp > 0) {
      this.mp = Math.min(s.maxMp, this.mp + mp);
      this.emitMp();
    }
    if (notify && hp > 0) this.events.emit(EV.toast, { message: `HP +${hp}`, tone: 'good' });
  }

  fullHeal(): void {
    const s = this.stats;
    this.hp = s.maxHp;
    this.mp = s.maxMp;
    this.emitHp();
    this.emitMp();
  }

  spendMp(cost: number): boolean {
    if (cost <= 0) return true;
    if (this.mp < cost) return false;
    this.mp -= cost;
    this.emitMp();
    return true;
  }

  /** AP 1을 해당 스탯에 넣는다. 성공 시 true */
  allocateAp(stat: PrimaryStat): boolean {
    if (this.ap.unspent < 1) {
      this.events.emit(EV.toast, { message: '남은 AP가 없습니다', tone: 'warn' });
      return false;
    }
    const before = this.stats;
    this.ap.unspent -= 1;
    this.ap[stat] += 1;
    const after = this.stats;
    this.hp = Math.min(after.maxHp, this.hp + Math.max(0, after.maxHp - before.maxHp));
    this.mp = Math.min(after.maxMp, this.mp + Math.max(0, after.maxMp - before.maxMp));
    this.emitHp();
    this.emitMp();
    this.emitStats();
    return true;
  }

  gainExp(rawAmount: number): void {
    if (this.level >= STATS.maxLevel) return;
    const amount = Math.max(1, Math.round(rawAmount * PROGRESSION.expRate));
    this.exp += amount;

    let leveled = 0;
    while (this.level < STATS.maxLevel && this.exp >= this.expNeeded) {
      this.exp -= this.expNeeded;
      this.level++;
      leveled++;
    }

    if (leveled > 0) {
      this.ap.unspent += leveled * STATS.ap.perLevel;
      this.sp += leveled * PROGRESSION.spPerLevel;
      this.fullHeal();
      this.events.emit(EV.levelUp, {
        level: this.level,
        stats: this.stats,
        apGained: leveled * STATS.ap.perLevel,
        spGained: leveled * PROGRESSION.spPerLevel,
      });
      this.emitStats();
      this.refreshTitles();
      this.refreshAchievements();
    }
    this.emitExp();
  }

  /** 이미 배율이 적용된 최종 금액을 넣는다 (배율은 드랍 생성 시점에 적용) */
  gainMeso(amount: number): void {
    this.meso += amount;
    this.events.emit(EV.mesoChanged, { meso: this.meso, delta: amount });
    this.refreshAchievements();
  }

  spendMeso(amount: number): boolean {
    if (this.meso < amount) return false;
    this.meso -= amount;
    this.events.emit(EV.mesoChanged, { meso: this.meso, delta: -amount });
    return true;
  }

  noteKill(monsterId: string): void {
    this.kills[monsterId] = (this.kills[monsterId] ?? 0) + 1;
    this.refreshTitles();
    this.refreshAchievements();
    let dirty = false;
    for (const [id, save] of Object.entries(this.quests)) {
      if (save.status !== 'active') continue;
      const def = getQuest(id);
      if (!def || def.kind !== 'hunt' || def.monsterId !== monsterId) continue;
      if (save.kills >= def.count) continue;
      save.kills += 1;
      dirty = true;
      if (save.kills >= def.count) {
        this.events.emit(EV.toast, { message: `${def.name} 보고 가능`, tone: 'good' });
      }
    }
    if (dirty) this.emitQuests();
  }

  questLine(questId: string): string {
    const def = getQuest(questId);
    if (!def) return '';
    const phase = questPhase(questId, this.quests, this.inventory);
    const progress = questProgress(def, this.quests[questId], this.inventory);
    const template =
      phase === 'offer'
        ? def.offer
        : phase === 'ready'
          ? def.ready
          : phase === 'done'
            ? def.done
            : def.active;
    return fillQuestText(template, progress, def.count);
  }

  acceptQuest(questId: string): boolean {
    if (questPhase(questId, this.quests, this.inventory) !== 'offer') return false;
    const def = getQuest(questId);
    if (!def) return false;
    this.quests[questId] = { status: 'active', kills: 0 };
    this.emitQuests();
    this.events.emit(EV.toast, { message: `퀘스트 수락: ${def.name}`, tone: 'good' });
    return true;
  }

  completeQuest(questId: string): boolean {
    if (questPhase(questId, this.quests, this.inventory) !== 'ready') return false;
    const def = getQuest(questId);
    if (!def) return false;
    if (def.kind === 'collect' && def.itemId) {
      if (!this.inventory.removeItem(def.itemId, def.count)) return false;
    }
    this.quests[questId] = { status: 'done', kills: this.quests[questId]?.kills ?? def.count };
    if (def.exp > 0) this.gainExp(def.exp);
    if (def.meso > 0) this.gainMeso(def.meso);
    for (const reward of def.items) {
      const left = this.inventory.add(reward.itemId, reward.count);
      if (left > 0) {
        this.events.emit(EV.toast, { message: '인벤토리가 꽉 차 일부 보상을 받지 못했습니다', tone: 'warn' });
      }
    }
    this.emitQuests();
    this.events.emit(EV.toast, { message: `퀘스트 완료: ${def.name}`, tone: 'good' });
    this.refreshTitles();
    this.refreshAchievements();
    return true;
  }

  sellToShop(itemId: string, _count = 1): boolean {
    const found = this.inventory.findSlot(itemId);
    if (!found) {
      this.events.emit(EV.toast, { message: '팔 아이템이 없습니다', tone: 'warn' });
      return false;
    }
    return this.sellSlot(found.tab, found.index);
  }

  sellSlot(tab: ItemTab, index: number): boolean {
    const slot = this.inventory.slots(tab)[index];
    if (!slot) return false;
    const item = ITEMS[slot.itemId];
    if (!item) return false;
    const price = slot.gear ? gearPrice(slot.gear) : item.price;
    if (!this.inventory.removeAt(tab, index, 1)) return false;
    this.gainMeso(price);
    this.events.emit(EV.toast, { message: `${item.name} 판매 (+${price.toLocaleString()} 메소)`, tone: 'info' });
    return true;
  }

  buyFromShop(shopId: string, itemId: string): boolean {
    const shop = getShop(shopId);
    const item = ITEMS[itemId];
    if (!shop || !item || !shop.stock.includes(itemId)) return false;
    const sample = isGearItem(item) ? rollGear(itemId, 'common') : null;
    const price = Math.max(1, (sample ? gearPrice(sample) : item.price) * shop.buyMul);
    if (!this.spendMeso(price)) {
      this.events.emit(EV.toast, { message: '메소가 부족합니다', tone: 'warn' });
      return false;
    }
    const left = this.inventory.add(itemId, 1, sample ?? undefined);
    if (left > 0) {
      this.gainMeso(price);
      this.events.emit(EV.toast, { message: '인벤토리가 꽉 찼습니다', tone: 'warn' });
      return false;
    }
    this.events.emit(EV.toast, { message: `${item.name} 구매 (${price.toLocaleString()} 메소)`, tone: 'good' });
    return true;
  }

  /** 장비 탭 아이템을 착용한다. 주문서 대기 중이면 강화를 시도한다. */
  equipWeapon(tab: ItemTab, index: number): boolean {
    return this.useEquipOrScroll(tab, index);
  }

  useEquipOrScroll(tab: ItemTab, index: number): boolean {
    const slot = this.inventory.slots(tab)[index];
    if (!slot) return false;
    const def = ITEMS[slot.itemId];
    if (this.pendingScroll && !(def?.scroll)) {
      return this.applyPendingToBag(tab, index);
    }
    if (def?.scroll) {
      this.pendingScroll = { tab, index };
      this.events.emit(EV.toast, { message: `${def.name}: 강화할 장비를 클릭하세요`, tone: 'info' });
      this.events.emit(EV.gearChanged, {});
      return true;
    }
    if (!def?.equip?.slot) {
      this.events.emit(EV.toast, { message: `${def?.name ?? '아이템'}: 장착할 수 없습니다`, tone: 'warn' });
      return false;
    }
    return this.wearFromBag(tab, index);
  }

  wearFromBag(tab: ItemTab, index: number): boolean {
    const taken = this.inventory.takeAt(tab, index);
    if (!taken) return false;
    const def = ITEMS[taken.itemId];
    const slot = def?.equip?.slot;
    if (!slot) {
      this.inventory.put(taken);
      return false;
    }
    const incoming = taken.gear ?? rollGear(taken.itemId);
    if (!incoming) {
      this.inventory.put(taken);
      return false;
    }
    const current = this.worn[slot];
    if (current && !this.inventory.put({ itemId: current.itemId, count: 1, gear: current })) {
      this.inventory.put(taken);
      this.events.emit(EV.toast, { message: '인벤토리가 꽉 찼습니다', tone: 'warn' });
      return false;
    }
    this.worn[slot] = incoming;
    if (slot === 'weapon' && def.equip?.weaponId) this.weaponId = def.equip.weaponId;
    this.emitStats();
    this.events.emit(EV.gearChanged, {});
    this.events.emit(EV.toast, { message: `${def.name} 장착`, tone: 'good' });
    return true;
  }

  unequip(slot: EquipSlot): boolean {
    if (slot === 'weapon') {
      this.events.emit(EV.toast, { message: '무기는 다른 무기로만 교체합니다', tone: 'warn' });
      return false;
    }
    const current = this.worn[slot];
    if (!current) return false;
    if (!this.inventory.put({ itemId: current.itemId, count: 1, gear: current })) {
      this.events.emit(EV.toast, { message: '인벤토리가 꽉 찼습니다', tone: 'warn' });
      return false;
    }
    this.worn[slot] = null;
    this.emitStats();
    this.events.emit(EV.gearChanged, {});
    return true;
  }

  applyPendingToWorn(slot: EquipSlot): boolean {
    if (!this.pendingScroll) return false;
    const piece = this.worn[slot];
    if (!piece) return false;
    return this.runScroll(piece, () => {
      this.worn[slot] = piece.upgradesLeft >= 0 ? piece : piece;
    }, () => {
      this.worn[slot] = null;
      if (slot === 'weapon') {
        const fallback = starterGear(getWeapon(this.job.weaponId).itemId);
        if (fallback) {
          this.worn.weapon = fallback;
          this.weaponId = this.job.weaponId;
        }
      }
    });
  }

  applyPendingToBag(tab: ItemTab, index: number): boolean {
    const slot = this.inventory.slots(tab)[index];
    if (!this.pendingScroll || !slot?.gear) return false;
    return this.runScroll(slot.gear, () => undefined, () => {
      this.inventory.removeAt(tab, index, 1);
    });
  }

  private runScroll(
    piece: GearPiece,
    onKeep: () => void,
    onBoom: () => void,
  ): boolean {
    if (!this.pendingScroll) return false;
    const scrollSlot = this.inventory.slots(this.pendingScroll.tab)[this.pendingScroll.index];
    const scrollDef = ITEMS[scrollSlot?.itemId ?? ''];
    const spec = scrollDef?.scroll;
    if (!spec || !scrollSlot) {
      this.pendingScroll = null;
      return false;
    }
    const result = applyScrollToGear(piece, spec);
    if (result === 'blocked') {
      this.events.emit(EV.toast, { message: '이 주문서와 장비가 맞지 않거나 횟수가 없습니다', tone: 'warn' });
      return false;
    }
    this.inventory.removeAt(this.pendingScroll.tab, this.pendingScroll.index, 1);
    this.pendingScroll = null;
    if (result === 'boom') {
      onBoom();
      this.events.emit(EV.toast, { message: `${ITEMS[piece.itemId]?.name ?? '장비'}가 파괴되었습니다`, tone: 'warn' });
    } else if (result === 'success') {
      onKeep();
      this.events.emit(EV.toast, { message: `강화 성공! (+${piece.successCount})`, tone: 'good' });
    } else {
      onKeep();
      this.events.emit(EV.toast, { message: '강화 실패', tone: 'warn' });
    }
    this.emitStats();
    this.events.emit(EV.gearChanged, {});
    return true;
  }

  /** 소비 아이템 사용. 성공 시 true */
  useItem(tab: ItemTab, index: number): boolean {
    const slot = this.inventory.slots(tab)[index];
    if (!slot) return false;
    const def = ITEMS[slot.itemId];
    if (def?.scroll) return this.useEquipOrScroll(tab, index);
    if (!def?.use) {
      this.events.emit(EV.toast, { message: `${def?.name ?? '아이템'}: 사용할 수 없습니다`, tone: 'warn' });
      return false;
    }
    const s = this.stats;
    const healsHp = (def.use.hp ?? 0) > 0;
    const healsMp = (def.use.mp ?? 0) > 0;
    const hpFull = this.hp >= s.maxHp;
    const mpFull = this.mp >= s.maxMp;
    if ((!healsHp || hpFull) && (!healsMp || mpFull)) {
      this.events.emit(EV.toast, { message: '이미 가득 찼습니다', tone: 'warn' });
      return false;
    }
    this.inventory.removeAt(tab, index, 1);
    this.heal(def.use.hp ?? 0, def.use.mp ?? 0, false);
    return true;
  }

  /** 사망 시 현재 레벨 요구량의 일부를 깎는다. 실제 깎인 양을 반환 */
  applyDeathPenalty(): number {
    if (this.level >= STATS.maxLevel) return 0;
    const need = this.expNeeded;
    const lost = Math.min(this.exp, Math.floor(need * RESPAWN.expPenaltyRate));
    if (lost <= 0) {
      this.emitExp();
      return 0;
    }
    this.exp -= lost;
    this.emitExp();
    return lost;
  }

  respawn(ratio: number): void {
    const s = this.stats;
    this.hp = Math.max(1, Math.floor(s.maxHp * ratio));
    this.mp = Math.max(0, Math.floor(s.maxMp * ratio));
    this.clearStatuses();
    this.clearBuffs();
    this.emitHp();
    this.emitMp();
    this.events.emit(EV.playerRespawned, {});
  }

  emitAll(): void {
    this.emitHp();
    this.emitMp();
    this.emitExp();
    this.emitStats();
    this.emitStatus();
    this.events.emit(EV.mesoChanged, { meso: this.meso, delta: 0 });
    this.emitQuests();
    this.events.emit(EV.gearChanged, {});
    this.events.emit(EV.titleChanged, {});
    this.events.emit(EV.achieveChanged, {});
  }

  private emitQuests(): void {
    this.events.emit(EV.questChanged, {});
  }

  private emitHp(): void {
    this.events.emit(EV.hpChanged, { hp: this.hp, maxHp: this.stats.maxHp });
  }

  private emitMp(): void {
    this.events.emit(EV.mpChanged, { mp: this.mp, maxMp: this.stats.maxMp });
  }

  private emitExp(): void {
    const need = this.expNeeded;
    this.events.emit(EV.expChanged, {
      exp: this.exp,
      need,
      ratio: need > 0 ? this.exp / need : 1,
      level: this.level,
    });
  }

  private emitStats(): void {
    this.events.emit(EV.statsChanged, {
      unspentAp: this.ap.unspent,
      unspentSp: this.sp,
      level: this.level,
      jobName: this.job.name,
    });
  }

  private emitStatus(): void {
    this.events.emit(EV.statusChanged, { effects: this.activeStatuses() });
  }

  private tickStatuses(deltaMs: number): void {
    let dirty = false;
    for (const id of [...this.effects.keys()]) {
      const fx = this.effects.get(id);
      if (!fx) continue;
      const def = STATUSES[id];
      fx.remainMs -= deltaMs;
      if (def.tickMs && def.tickDamage) {
        fx.tickAcc += deltaMs;
        while (fx.tickAcc >= def.tickMs && this.alive) {
          fx.tickAcc -= def.tickMs;
          const amount = Math.max(1, def.tickDamage);
          this.damage(amount);
          this.events.emit(EV.statusTick, { amount, name: def.name });
        }
      }
      if (fx.remainMs <= 0 || !this.alive) {
        this.effects.delete(id);
        dirty = true;
      }
    }
    if (dirty) this.emitStatus();
  }

  toJSON(): SaveData {
    return {
      version: SAVE_VERSION,
      name: this.name,
      titleId: this.titleId,
      unlockedTitles: [...this.unlockedTitles],
      unlockedAchievements: [...this.unlockedAchievements],
      maxCombo: this.maxCombo,
      kills: { ...this.kills },
      level: this.level,
      exp: this.exp,
      hp: this.hp,
      mp: this.mp,
      meso: this.meso,
      inventory: this.inventory.toJSON(),
      mapId: this.mapId,
      spawnId: this.spawnId,
      playtimeMs: this.playtimeMs,
      savedAt: Date.now(),
      ap: { ...this.ap },
      weaponId: this.weaponId,
      jobId: this.jobId,
      sp: this.sp,
      skillLevels: { ...this.skillLevels },
      quickSlots: [...this.quickSlots],
      quests: { ...this.quests },
      worn: { ...this.worn },
    };
  }

  load(data: SaveData): void {
    this.name = data.name?.trim() || PLAYER.defaultName;
    this.titleId = data.titleId || DEFAULT_TITLE;
    this.unlockedTitles = Array.isArray(data.unlockedTitles) && data.unlockedTitles.length
      ? [...new Set([DEFAULT_TITLE, ...data.unlockedTitles])]
      : [DEFAULT_TITLE];
    this.unlockedAchievements = Array.isArray(data.unlockedAchievements) ? [...data.unlockedAchievements] : [];
    this.maxCombo = Math.max(0, data.maxCombo ?? 0);
    this.kills = { ...(data.kills ?? {}) };
    this.jobId = getJob(data.jobId).id;
    this.level = Math.min(STATS.maxLevel, Math.max(1, data.level));
    this.exp = Math.max(0, data.exp);
    this.meso = Math.max(0, data.meso);
    this.ap = clampAp(data.ap, this.level, this.jobId);
    this.weaponId = getWeapon(data.weaponId).id;
    this.sp = Math.max(0, Math.floor(data.sp ?? 0));
    this.skillLevels = { ...(data.skillLevels ?? {}) };
    this.quickSlots = [...(data.quickSlots ?? getJob(this.jobId).quickSlots)];
    this.quests = { ...(data.quests ?? {}) };
    this.worn = { ...(data.worn ?? {}) };
    if (!this.worn.weapon) {
      const piece = starterGear(getWeapon(this.weaponId).itemId);
      if (piece) this.worn.weapon = piece;
    } else {
      this.weaponId = ITEMS[this.worn.weapon.itemId]?.equip?.weaponId ?? this.weaponId;
    }
    const s = this.stats;
    this.hp = Phaser.Math.Clamp(data.hp, 1, s.maxHp);
    this.mp = Phaser.Math.Clamp(data.mp, 0, s.maxMp);
    this.playtimeMs = data.playtimeMs ?? 0;
    this.mapId = data.mapId;
    this.spawnId = data.spawnId;
    this.inventory.load(data.inventory ?? []);
    this.clearBuffs();
    this.refreshTitles(true);
    this.refreshAchievements(true);
    this.emitAll();
  }

  /**
   * 전직관에서 다음 차수로 바꾼다. 레벨·nextIds·바로 다음 차수만 허용.
   * 스킬 레벨·SP·AP·인벤은 유지하고 startLevel 로 되돌리지 않는다.
   */
  advanceJob(nextId: string): boolean {
    const current = getJob(this.jobId);
    if (!current.nextIds.includes(nextId)) return false;
    const next = JOBS[nextId];
    if (!next || next.rank !== current.rank + 1) return false;
    const req = requiredLevel(this.jobId);
    if (req === null || this.level < req) return false;
    this.jobId = next.id;
    this.emitStats();
    this.events.emit(EV.toast, { message: `전직: ${next.name}`, tone: 'good' });
    return true;
  }

  /** 타이틀에서 직업을 고르고 새 게임을 시작한다 */
  beginJob(jobId: string, name = PLAYER.defaultName): void {
    const id = isFirstJob(jobId) ? getJob(jobId).id : DEFAULT_JOB_ID;
    this.applyJobDefaults(id, true);
    const s = this.stats;
    this.name = name.trim() || PLAYER.defaultName;
    this.titleId = DEFAULT_TITLE;
    this.unlockedTitles = [DEFAULT_TITLE];
    this.unlockedAchievements = [];
    this.maxCombo = 0;
    this.kills = {};
    this.level = PLAYER.startLevel;
    this.exp = 0;
    this.meso = STARTING_MESO;
    this.playtimeMs = 0;
    this.mapId = '';
    this.spawnId = '';
    this.quests = {};
    this.pendingScroll = null;
    this.hp = s.maxHp;
    this.mp = s.maxMp;
    this.clearStatuses();
    this.clearBuffs();
    this.emitAll();
  }

  equipTitle(id: string): boolean {
    if (!this.unlockedTitles.includes(id) || !TITLES[id]) return false;
    this.titleId = id;
    this.emitTitle();
    return true;
  }

  refreshTitles(silent = false): void {
    const ctx = { level: this.level, kills: this.kills, quests: this.quests };
    const next = [...this.unlockedTitles];
    for (const id of TITLE_ORDER) {
      const def = TITLES[id];
      if (!def || next.includes(id)) continue;
      if (!isTitleEarned(def, ctx)) continue;
      next.push(id);
      if (!silent) this.events.emit(EV.toast, { message: `칭호 획득: ${def.name}`, tone: 'good' });
    }
    this.unlockedTitles = next;
    if (!this.unlockedTitles.includes(this.titleId)) this.titleId = DEFAULT_TITLE;
    this.emitTitle();
  }

  noteCombo(count: number): void {
    if (count <= this.maxCombo) return;
    this.maxCombo = count;
    this.refreshAchievements();
  }

  refreshAchievements(silent = false): void {
    const ctx = {
      level: this.level,
      meso: this.meso,
      maxCombo: this.maxCombo,
      kills: this.kills,
      quests: this.quests,
    };
    const next = [...this.unlockedAchievements];
    for (const id of ACHIEVE_ORDER) {
      const def = ACHIEVEMENTS[id];
      if (!def || next.includes(id)) continue;
      if (!isAchieveEarned(def, ctx)) continue;
      next.push(id);
      if (!silent) this.events.emit(EV.toast, { message: `도전 과제: ${def.name}`, tone: 'good' });
    }
    this.unlockedAchievements = next;
    this.events.emit(EV.achieveChanged, {});
  }

  private emitTitle(): void {
    this.events.emit(EV.titleChanged, {});
  }

  skillLevel(id: string): number {
    return Math.max(0, Math.floor(this.skillLevels[id] ?? 0));
  }

  resolvedSkill(id: string) {
    return resolveSkill(id, this.skillLevel(id));
  }

  /** Ctrl 기본 공격. 직업의 basicAttack 스타일을 따른다. */
  basicAttack() {
    return basicAttackForJob(this.jobId);
  }

  quickSkill(index: number) {
    const id = this.quickSlots[index];
    if (!id) return undefined;
    return this.resolvedSkill(id);
  }

  allocateSp(skillId: string): boolean {
    const entry = getSkillEntry(skillId);
    if (!entry || !jobOwnsSkill(this.jobId, entry.jobId)) {
      this.events.emit(EV.toast, { message: '이 직업의 스킬이 아닙니다', tone: 'warn' });
      return false;
    }
    if (this.sp < 1) {
      this.events.emit(EV.toast, { message: '남은 SP가 없습니다', tone: 'warn' });
      return false;
    }
    const current = this.skillLevel(skillId);
    if (current >= entry.maxLevel) {
      this.events.emit(EV.toast, { message: '이미 마스터했습니다', tone: 'warn' });
      return false;
    }
    this.sp -= 1;
    this.skillLevels[skillId] = current + 1;
    this.emitStats();
    this.events.emit(EV.toast, { message: `${entry.name} Lv.${current + 1}`, tone: 'good' });
    return true;
  }

  bindQuickSlot(index: number, skillId: string): boolean {
    if (index < 0 || index > 2) return false;
    if (this.skillLevel(skillId) < 1) {
      this.events.emit(EV.toast, { message: '먼저 SP를 넣어 배우세요', tone: 'warn' });
      return false;
    }
    const next = [...this.quickSlots];
    while (next.length < 3) next.push('');
    next[index] = skillId;
    this.quickSlots = next;
    this.emitStats();
    return true;
  }

  applySkillBuff(skill: { buff?: { id: string; durationMs: number; defenseMul?: number; damageMul?: number; moveMul?: number }; name: string; icon: string }): void {
    const buff = skill.buff;
    if (!buff) return;
    this.buffs.set(buff.id, {
      remainMs: buff.durationMs,
      defenseMul: buff.defenseMul ?? 1,
      damageMul: buff.damageMul ?? 1,
      moveMul: buff.moveMul ?? 1,
      icon: skill.icon,
      name: skill.name,
    });
    this.emitStatus();
    this.events.emit(EV.toast, { message: `${skill.name} 발동`, tone: 'good' });
  }

  private applyJobDefaults(jobId: string, resetItems: boolean): void {
    const job = getJob(jobId);
    this.jobId = job.id;
    this.ap = createStartingAp(job.id);
    this.weaponId = job.weaponId;
    this.skillLevels = {};
    const attacks = job.skillIds.filter((id) => getSkillEntry(id)?.kind !== 'buff');
    for (const id of attacks.slice(0, 2)) this.skillLevels[id] = 1;
    this.sp = PROGRESSION.startSp;
    this.quickSlots = [...job.quickSlots];
    const weaponItem = getWeapon(job.weaponId).itemId;
    this.worn = { weapon: starterGear(weaponItem) };
    this.pendingScroll = null;
    if (resetItems) this.fillStarterItems();
  }

  private fillStarterItems(): void {
    this.inventory.load([]);
    const job = getJob(this.jobId);
    for (const entry of STARTING_INVENTORY) {
      this.inventory.add(entry.itemId, entry.count);
    }
    if (job.extraItemId) this.inventory.add(job.extraItemId, 1);
  }

  private clearBuffs(): void {
    if (this.buffs.size === 0) return;
    this.buffs.clear();
    this.emitStatus();
  }

  private tickBuffs(deltaMs: number): void {
    let dirty = false;
    for (const id of [...this.buffs.keys()]) {
      const fx = this.buffs.get(id);
      if (!fx) continue;
      fx.remainMs -= deltaMs;
      if (fx.remainMs <= 0 || !this.alive) {
        this.buffs.delete(id);
        dirty = true;
      }
    }
    if (dirty) this.emitStatus();
  }
}
